/**
 * PGSS-5158 — an order holding Inventory may only hold lines that can be filled from stock.
 *
 * Product answers, 29 Aug 2026. Full spec:
 * docs/design/mixed-order-restriction-PGSS-5158.md (psg-backend, docs/3.0.2-scope-review).
 *
 *   Inventory only / Custom only / Standard only ......... allowed
 *   Custom + Standard, no Inventory ..................... allowed
 *   Inventory + Standard, every Standard line in stock .. allowed
 *   Inventory + Standard, any Standard line short ....... blocked
 *   Inventory + Custom .................................. blocked, no exception
 *
 * The stock exception is real and is not a loophole: "non-stock" classifies the product, it does
 * not describe the shelf. A cancelled order returns units to stock, and that Standard line then
 * behaves exactly like an inventory line. Custom is made to order and never qualifies.
 */

/** Settings category the flag lives in. */
export const MIXED_ORDER_SETTING_CATEGORY = 'order';

/**
 * The key as it comes back from GET /api/setting — the lowercased `settings.field`.
 *
 * Both spellings are accepted because the seed row does not exist yet and whoever creates it may
 * write the field either way. Reading only one and getting the other would silently leave the
 * guard permanently on with no way to turn it off, which is the failure this whole flag exists to
 * avoid.
 */
export const MIXED_ORDER_SETTING_KEYS = [
  'block mixed inventory orders',
  'block_mixed_inventory_orders',
];

/** Pull the flag out of a GET /api/setting?category=order response. */
export const readMixedOrderSetting = (settingsResponse) => {
  if (!settingsResponse) return undefined;
  const key = MIXED_ORDER_SETTING_KEYS.find((k) => settingsResponse[k] !== undefined);
  return key ? settingsResponse[key] : undefined;
};

/** What an OFF switch looks like in the settings table. */
const OFF_VALUES = ['false', '0', 'no', 'off'];

/**
 * Is the guard on?
 *
 * Reads as ON unless the row explicitly says otherwise — the opposite way round from a normal
 * boolean, and deliberately so. The seed row will not exist on a fresh database or a restored
 * snapshot, and treating "missing" as OFF would silently disable the guard there with nothing to
 * show for it.
 *
 * ⚠️ Settings toggles in this system are stored as `'0'` / `'1'` (field_type 0), not
 * `'true'` / `'false'`. Recognising only `'false'` would mean an admin switching the toggle OFF
 * saw no effect at all — the flag would look switchable and never switch.
 *
 * @param {string|boolean|number|undefined} settingValue raw value from the settings table
 * @returns {boolean}
 */
export const isMixedOrderGuardEnabled = (settingValue) => {
  if (settingValue === undefined || settingValue === null || settingValue === '') return true;
  return !OFF_VALUES.includes(String(settingValue).trim().toLowerCase());
};

/**
 * Classify one order line.
 *
 * ⚠️ Order matters and mirrors the backend's `isInventoryProduct()`: a product carrying BOTH
 * `is_custom` and `is_stock_product` is Custom. Reading it as Inventory would hide the
 * "Add Custom Product" button on an order that is entirely custom, and — worse — would permit the
 * Inventory + Custom order this rule exists to stop.
 *
 * @param {object} line an order_details row
 * @returns {'custom'|'inventory'|'standard'}
 */
export const classifyLine = (line) => {
  const product = line?.product || line || {};
  if (product.is_custom === true || product.is_custom === 1) return 'custom';
  if (product.is_stock_product === true || product.is_stock_product === 1) return 'inventory';
  return 'standard';
};

/** Available stock for a line. `units_in_stock` is already net of committed quantity. */
const confirmedQuantity = (line) => {
  const value = Number(line?.confirmed_quantity);
  return Number.isFinite(value) ? value : 0;
};

/**
 * Stock this line may draw on, INCLUDING what it already holds.
 *
 * `units_in_stock` is net of committed quantity: InventoryUtils decrements it by the quantity
 * confirmed to this very order detail. So a saved, fully-allocated line reads back as
 * `units_in_stock = 0` while being perfectly healthy -- ten units ordered against ten in stock
 * leaves zero, and comparing that zero against the ordered ten calls a good order short.
 *
 * Adding the line's own `confirmed_quantity` back gives the figure the staff member is actually
 * choosing from. A line being added now has no allocation yet (`confirmed_quantity` absent, or 0
 * from the response default), so it is unaffected and still sees only genuinely free stock.
 */
const availableStock = (line) => {
  const product = line?.product || line || {};
  const value = Number(product.units_in_stock);
  return (Number.isFinite(value) ? value : 0) + confirmedQuantity(line);
};

const orderedQuantity = (line) => {
  const value = Number(line?.quantity ?? line?.order_quantity);
  return Number.isFinite(value) ? value : 0;
};

/**
 * Standard lines that cannot be filled from stock. Only meaningful once the order holds Inventory.
 *
 * Compared against `availableStock`, which re-adds the line's own allocation -- see the note there
 * for why the raw `units_in_stock` would flag healthy saved orders.
 *
 * `>=`, not `==`. A cancelled order freeing ten units must not force the next customer to take
 * exactly ten — that would block a legitimate partial sale and strand the rest.
 */
export const findShortStockLines = (orderDetails = []) => {
  const lines = orderDetails || [];
  if (!lines.some((line) => classifyLine(line) === 'inventory')) return [];
  return lines
    .filter((line) => classifyLine(line) === 'standard')
    .filter((line) => availableStock(line) < orderedQuantity(line))
    .map((line) => ({
      line,
      available: availableStock(line),
      ordered: orderedQuantity(line),
      name: line?.product?.name || line?.name || 'this item',
    }));
};

export const CUSTOM_BLOCKED_REASON =
  'This order contains inventory items. Custom products go on a separate order.';

/**
 * PGSS-5467: shown above the picker, which stays usable.
 *
 * The previous copy said catalogue items needed a separate order too. They do not -- Custom +
 * Standard is an allowed combination, and replacing the whole panel made it unbuildable.
 */
export const PRODUCTS_LIMITED_NOTE =
  'This order contains a custom product, so inventory items are not shown. '
  + 'Catalogue items can still be added.';

/**
 * PGSS-5467 (QA re-test): shown when an inventory item is added to an order that holds a custom line.
 *
 * The picker is normally narrowed by the server, so this never fires -- it is the guarantee that the
 * rule still holds when the narrowing does not. `stock_product` is a request the API may not honour:
 * an environment running an older backend ignores the parameter, and a product whose
 * `is_stock_product` was never written reads back NULL and is served as non-stock by design. Either
 * way an inventory row reaches the picker, and without this it could be added -- silently building
 * the one combination the rule forbids with no exception.
 *
 * Not phrased as an error. The order is valid and nothing has gone wrong; the combination is simply
 * not one this screen may build, and the sentence says what to do instead.
 */
export const INVENTORY_BLOCKED_REASON =
  'This is an inventory item and this order contains a custom product, so it cannot be added here. '
  + 'Order it separately, or remove the custom product first.';

/**
 * PGSS-5467: is this catalogue row one the current order may not take?
 *
 * `restrictToNonStock` comes straight off the gate, so the answer is derived from the order's lines
 * on every call and needs no state of its own. Classification is `classifyLine`, the same reading
 * the gate and the backend use -- a product carrying both flags is Custom, never Inventory.
 *
 * @param {object} product a row from the product list
 * @param {boolean} restrictToNonStock the gate's flag for "this order holds a custom line"
 * @returns {boolean}
 */
export const isAddBlockedByCustomLine = (product, restrictToNonStock) =>
  Boolean(restrictToNonStock) && Boolean(product) && classifyLine(product) === 'inventory';

/** "Only 3 available. Reduce the quantity to 3 or fewer, or order this item separately." */
export const shortStockMessage = ({ available }) =>
  `Only ${available} available. Reduce the quantity to ${available} or fewer, `
  + 'or order this item separately.';

/**
 * Which add controls this order may still use.
 *
 * Derived from the current lines on every call — never a latched flag. Removing the last
 * constraining line restores the control and clears its note in the same render, so there is no
 * state to reset and no way to get stuck.
 *
 * An order that is ALREADY mixed is left alone entirely. Historical mixed orders exist — the rule
 * is UI-only, so the API, bulk import and RFQ conversion can still produce them — and blocking
 * their edit screen would strand them.
 *
 * PGSS-5467: a custom line restricts the product picker to non-stock items -- it does NOT close it.
 * Only Inventory + Custom is forbidden; Custom + Standard is allowed, and replacing the whole panel
 * made that permitted combination impossible to build on this screen.
 *
 * @param {Array} orderDetails current order_details
 * @param {boolean} enabled result of isMixedOrderGuardEnabled
 * @returns {{blockCustom: boolean, restrictToNonStock: boolean, alreadyMixed: boolean,
 *           customReason: string|null, productsNote: string|null}}
 */
export const getMixedOrderGate = (orderDetails = [], enabled = true) => {
  const none = {
    blockCustom: false,
    restrictToNonStock: false,
    alreadyMixed: false,
    customReason: null,
    productsNote: null,
  };
  if (!enabled) return none;

  const lines = orderDetails || [];
  const hasInventory = lines.some((line) => classifyLine(line) === 'inventory');
  const hasCustom = lines.some((line) => classifyLine(line) === 'custom');

  // Already mixed: leave every control alone so the order stays workable.
  if (hasInventory && hasCustom) return { ...none, alreadyMixed: true };

  return {
    blockCustom: hasInventory,
    restrictToNonStock: hasCustom,
    alreadyMixed: false,
    customReason: hasInventory ? CUSTOM_BLOCKED_REASON : null,
    productsNote: hasCustom ? PRODUCTS_LIMITED_NOTE : null,
  };
};
